import { useState, useEffect } from 'react';
import { X, CreditCard, DollarSign, Smartphone, Package, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { pagosCreditoApi } from '../lib/api';

interface PagarCreditoModalProps {
  bus: any;
  onClose: () => void;
  onPagado: (padron: string, nuevoSaldo: number, nuevoSaldoProductos: number, nuevoSaldoServicios: number) => void;
}

const METODOS = [
  { valor: 'efectivo', label: 'Efectivo' },
  { valor: 'yape_plin', label: 'Yape / Plin' },
  { valor: 'transferencia', label: 'Transferencia / Depósito' },
  { valor: 'pos', label: 'POS' },
];

const PagarCreditoModal = ({ bus, onClose, onPagado }: PagarCreditoModalProps) => {
  const deuda = parseFloat(bus.credito_usado || 0);
  const deudaProductos = parseFloat(bus.credito_usado_productos || 0);
  const deudaServicios = parseFloat(bus.credito_usado_servicios || 0);
  const [modoPago, setModoPago] = useState<'total' | 'parcial'>('total');
  const [montoProductos, setMontoProductos] = useState(deudaProductos.toFixed(2));
  const [montoServicios, setMontoServicios] = useState(deudaServicios.toFixed(2));
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [observaciones, setObservaciones] = useState('');
  const [pagadoPor, setPagadoPor] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [historial, setHistorial] = useState<any[]>([]);
  const [capturasYape, setCapturasYape] = useState<string[]>([]);
  const [mostrarModalCapturaYape, setMostrarModalCapturaYape] = useState(false);

  useEffect(() => {
    pagosCreditoApi.getByPadron(bus.padron).then(setHistorial).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bus.padron]);

  const elegirModo = (modo: 'total' | 'parcial') => {
    setModoPago(modo);
    if (modo === 'total') {
      setMontoProductos(deudaProductos.toFixed(2));
      setMontoServicios(deudaServicios.toFixed(2));
    }
  };

  const numProductos = parseFloat(montoProductos) || 0;
  const numServicios = parseFloat(montoServicios) || 0;
  const montoNumerico = numProductos + numServicios;
  const montoValido = montoNumerico > 0
    && numProductos <= deudaProductos + 0.001
    && numServicios <= deudaServicios + 0.001;
  const esYape = metodoPago === 'yape_plin';

  // Capturas de comprobante de pago (Yape): se leen como base64 y se mandan asi al
  // backend, que las guarda como archivos y las liga al pago. A diferencia de las
  // ventas, aca SI son obligatorias cuando el metodo es Yape (pueden ser una o mas).
  const agregarCapturasYape = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(file => {
      const lector = new FileReader();
      lector.onload = () => setCapturasYape(prev => [...prev, lector.result as string]);
      lector.readAsDataURL(file);
    });
  };

  const quitarCapturaYape = (index: number) => {
    setCapturasYape(prev => prev.filter((_, i) => i !== index));
  };

  const confirmar = async () => {
    if (!montoValido) {
      toast.error('Revisa los montos: ninguno puede superar la deuda pendiente de esa categoría, y al menos uno debe ser mayor a 0');
      return;
    }
    // La captura de Yape es opcional: no bloquea el registro del pago.
    setProcesando(true);
    try {
      const resultado = await pagosCreditoApi.crear(bus.padron, {
        monto_productos: numProductos,
        monto_servicios: numServicios,
        metodo_pago: metodoPago,
        observaciones: observaciones.trim() || undefined,
        pagado_por: pagadoPor.trim() || undefined,
        yape_capturas: esYape ? capturasYape : undefined,
      });
      toast.success(`Pago de S/ ${montoNumerico.toFixed(2)} registrado`);
      onPagado(bus.padron, resultado.credito_usado, resultado.credito_usado_productos, resultado.credito_usado_servicios);
      onClose();
    } catch (error: any) {
      toast.error('Error al registrar el pago: ' + error.message);
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
    >
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CreditCard className="text-blue-600" size={20} />
            Pagar crédito — Bus {bus.padron}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex justify-between items-center">
            <span className="text-sm text-red-700">Deuda actual</span>
            <span className="font-bold text-red-700 text-lg">S/ {deuda.toFixed(2)}</span>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Tipo de pago</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => elegirModo('total')}
                className={`p-3 rounded-lg border-2 text-sm font-medium ${modoPago === 'total' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'}`}
              >
                Pago total
              </button>
              <button
                onClick={() => elegirModo('parcial')}
                className={`p-3 rounded-lg border-2 text-sm font-medium ${modoPago === 'parcial' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'}`}
              >
                Pago parcial
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                <Package size={14} className="text-gray-500" /> Productos (S/)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max={deudaProductos}
                value={montoProductos}
                disabled={modoPago === 'total'}
                onChange={(e) => setMontoProductos(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">Debe: S/ {deudaProductos.toFixed(2)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                <Wrench size={14} className="text-gray-500" /> Servicios (S/)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max={deudaServicios}
                value={montoServicios}
                disabled={modoPago === 'total'}
                onChange={(e) => setMontoServicios(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">Debe: S/ {deudaServicios.toFixed(2)}</p>
            </div>
          </div>
          {modoPago === 'parcial' && (montoProductos !== '' || montoServicios !== '') && !montoValido && (
            <p className="text-xs text-red-600 -mt-2">Ningún monto puede superar la deuda pendiente de esa categoría, y al menos uno debe ser mayor a 0.</p>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Método de pago</label>
            <select value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)} className="w-full border rounded-lg px-3 py-2">
              {METODOS.map(m => <option key={m.valor} value={m.valor}>{m.label}</option>)}
            </select>
          </div>

          {esYape && (
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-purple-800">Comprobante de Yape (opcional)</h4>
                <button
                  type="button"
                  onClick={() => setMostrarModalCapturaYape(true)}
                  className="text-xs px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  + Agregar captura
                </button>
              </div>
              {capturasYape.length === 0 ? (
                <p className="text-xs text-purple-700">
                  Sin capturas todavía. Es opcional, podés registrar el pago sin adjuntarla.
                </p>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {capturasYape.map((captura, i) => (
                    <div key={i} className="relative">
                      <img src={captura} alt={`Captura ${i + 1}`} className="w-full h-16 object-cover rounded border" />
                      <button
                        type="button"
                        onClick={() => quitarCapturaYape(i)}
                        className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">¿Quién trae el dinero? <span className="text-gray-400 font-normal">(opcional, si no es el dueño — ej. el chofer)</span></label>
            <input type="text" value={pagadoPor} onChange={(e) => setPagadoPor(e.target.value)} maxLength={80}
              className="w-full border rounded-lg px-3 py-2" placeholder="Nombre de quien paga" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Observaciones (opcional)</label>
            <textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
              rows={2}
            />
          </div>

          <div className="border-t pt-3 flex justify-between text-sm">
            <span className="text-gray-600">Saldo después del pago</span>
            <span className="font-bold">S/ {Math.max(0, deuda - montoNumerico).toFixed(2)}</span>
          </div>

          {historial.length > 0 && (
            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-gray-500 mb-2">Pagos anteriores de este bus</p>
              <div className="max-h-28 overflow-y-auto space-y-1">
                {historial.slice(0, 5).map((p) => (
                  <div key={p.id} className="flex justify-between text-xs text-gray-600">
                    <span>{new Date(p.fecha).toLocaleDateString('es-PE')}</span>
                    <span className="font-medium text-green-600">S/ {parseFloat(p.monto).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={confirmar}
            disabled={procesando || !montoValido}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <DollarSign size={18} />
            {procesando ? 'Procesando...' : `Registrar pago de S/ ${montoNumerico.toFixed(2)}`}
          </button>
        </div>
      </div>

      {/* Modal para agregar capturas de comprobante Yape (opcional) */}
      {mostrarModalCapturaYape && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]"
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-purple-600" />
                Capturas de comprobante Yape
              </h3>
              <button onClick={() => setMostrarModalCapturaYape(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <label className="flex flex-col items-center justify-center border-2 border-dashed border-purple-300 rounded-lg py-8 cursor-pointer hover:bg-purple-50">
              <Smartphone className="w-8 h-8 text-purple-400 mb-2" />
              <span className="text-sm text-purple-700 font-medium">Click para elegir captura(s) de pantalla</span>
              <span className="text-xs text-gray-500 mt-1">Podés seleccionar varias a la vez</span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => { agregarCapturasYape(e.target.files); e.target.value = ''; }}
              />
            </label>

            {capturasYape.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-4">
                {capturasYape.map((captura, i) => (
                  <div key={i} className="relative">
                    <img src={captura} alt={`Captura ${i + 1}`} className="w-full h-24 object-cover rounded border" />
                    <button
                      type="button"
                      onClick={() => quitarCapturaYape(i)}
                      className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => setMostrarModalCapturaYape(false)}
              className="w-full mt-6 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Listo ({capturasYape.length} captura{capturasYape.length === 1 ? '' : 's'})
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PagarCreditoModal;
